<template>
    <!-- Immigration comparison Model -->
    <b-modal
        id="immigration-modal"
        centered
        size="lg"
        hide-header
        hide-footer
        :modal-class="{animationsActive: animationsToggle}"
        :no-fade="!animationsToggle"
        @shown="getCarouselWidth(); setScrollColor()"
        @hidden="initScrollColor"
        @hide="resetBodyPosition"
    >
        <div class="immigration-content d-flex flex-column align-items-center p-0" v-bind:class="[fontSizeSetting, {openDyslexicFont: openDyslexicToggle}]">
            <div class="immigration-header mb-4 mt-2 col-12 col-sm-7 d-flex flex-column align-items-center">
                <b-button class="mt-3 close" aria-label="Close"  block @click="$bvModal.hide('immigration-modal')">X</b-button>
                <h2 class="col-12 m-0 mb-4 px-2 px-sm-0 text-center modal-title" :style="'--category-color:' + categoryInfo.color"> {{ $t('immigration-flows') }} </h2>
                <div
                    class="col-10 col-sm-11 col-lg-8 btn d-flex justify-content-between align-items-center date-range"
                    :style="'--category-color:' + categoryInfo.color"
                    v-if="dates"
                >
                    <router-link
                        :to="{ name: '' }"
                        @click.native="changePeriod('next')"
                        :class="dates.lastOne ? 'disabled' : null"
                        :aria-label="$t('next-date-range')"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-left']" />
                    </router-link>
                    <p class="m-0 date-range"> {{ `${dates.from} - ${dates.to}` }} </p>
                    <router-link
                        :to="{ name: '' }"
                        @click.native="changePeriod('previous')"
                        :class="dates.firstOne ? 'disabled' : null"
                        :aria-label="$t('previous-date-range')"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-right']"/>
                    </router-link>
                </div>
            </div>
            <div class="immigration-body col-12 mb-4 col-sm-7 d-flex flex-column align-items-center" v-if="comparingData.region !== null">
                <div class="carousel" ref="carousel" :style="'--color:' + scrollColor">
                    <div class="slides" :style="`--left:${positionLeft}px`">
                        <div
                            v-for="(region, index) in rankingData"
                            :aria-hidden="index === comparingData.region.index ? false : true"
                            :key="region.id" class="slide-elements"
                            :style="`--min-width: ${carouselWidth}px`"
                        >
                            <div class="map m-0 p-0 pb-3 col-12 text-center">
                                <img :src="require(`@/assets/images/immigration/${region.id}.png`)" :alt="$t('world-map-with') + $t(region.description.text, { n: '' }) + $t('world-map-highlight')">
                            </div>
                            <div class="bottom m-0 p-0 pb-3 col-12 row justify-content-center" :style="'--category-color:' + categoryInfo.color">
                                <div class="position-section col-12 p-0 mb-4 row justify-content-center">
                                    <div class="col-3 text-center align-self-center">
                                        <span class="big-text m-0 text-white" :aria-label="$t('rank') +' '+region.place"> {{ region.place }} </span>
                                    </div>
                                    <div class="col-9 col-lg-9 align-self-center">
                                        <p class="m-0" v-if="$i18n.locale === 'fr' || categoryInfo.config.multiple === null">
                                            {{ vueNumberFormat(region.value, {
                                                    precision:      categoryInfo.config.precision,
                                                    prefix:         categoryInfo.config.prefix,
                                                    suffix:         categoryInfo.config.suffix,
                                                    decimal:        categoryInfo.config.decimal,
                                                    thousand:       $t('thousand-format'),
                                                    acceptNegative: false,
                                                    isInteger:      false
                                                })
                                            }}
                                        </p>
                                        <p class="m-0 text-white">
                                            {{ $t(region.description.text, {first: $t(`${region.description.plural}.first`), second: $t(`${region.description.plural}.second`), third: $t(`${region.description.plural}.third`)} ) }}
                                        </p>
                                    </div>
                                </div>
                                <div class="col-12 p-0 row justify-content-center">
                                    <div class="col-3">
                                        <hr class="timeline">
                                    </div>
                                    <div class="col-9 col-lg-9" v-if="region.description.details !== null">
                                        <p class="m-0 country-section">
                                            {{ $t('including') }}
                                            <span>
                                                {{ $t(region.description.details) }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="immigration-footer col-12 px-3 px-sm-5 py-4" v-if="comparingData.region !== null">
                <div class="position-changer d-flex justify-content-between align-items-center">
                    <router-link
                        :to="{ name: '' }"
                        :aria-label="$t('previous-ranking')"
                        @click.native="changeComparedWith('previous')"
                        class="previous-ranking"
                        :class="comparingData.region.firstOne ? 'disabled' : null"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-left']"  />
                    </router-link>
                    <p class="m-0"> {{ $t('next-position') }} </p>
                    <router-link
                        :to="{ name: '' }"
                        :aria-label="$t('next-ranking')"
                        @click.native="changeComparedWith('next')"
                        class="next-ranking"
                        :class="comparingData.region.lastOne ? 'disabled' : null"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-right']"  />
                    </router-link>
                </div>
            </div>
        </div>
    </b-modal>
</template>
<script>

// Helpers
import { EventBus } from '@/helpers/EventBus.js';
import { Default }  from '@/helpers/Default.js';

export default {
    props: [ 'categoryInfo', 'comparingData', 'rankingData', 'dates' ],
    data () {
        return {
            animationsToggle:   Default.animationsToggle,
            openDyslexicToggle: Default.openDyslexicToggle,
            fontSizeSetting:    Default.size,
            positionLeft:       0,
            carouselWidth:      0,
            scrollColor:        '#030017',
        };
    },
    mounted() {
        if (this.$session.has('openDyslexicToggle')) {
            this.openDyslexicToggle = this.$session.get('openDyslexicToggle');
        }

        if (this.$session.has('animationsToggle')) {
            this.animationsToggle = this.$session.get('animationsToggle');
        }

        if (this.$session.has('growFont')) {
            this.fontSizeSetting = this.$session.get('growFont');
        }

        // Listen for OpenDyslexic font setting value
        EventBus.$on('settingsChanged', (setting) => {
            this[setting.name] = setting.value;
        });

        // Update the setting window too when the font size changes
        EventBus.$on('grow-font', this.changeFontSize);

        window.addEventListener('resize', this.getCarouselWidth);

    },
    methods: {
        /* Update Carousel element size and Ranking position with screen resize */
        getCarouselWidth () {
            if (this.$refs.carousel === undefined ) {
                return;
            }
            this.carouselWidth = this.$refs.carousel.clientWidth;
            this.positionLeft = -this.carouselWidth * this.comparingData.region.index;
        },

        /**
         * Keep the position of the scroll when modal close 
         */
        resetBodyPosition () {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            window.scrollTo({
                top: parseInt(scrollY || '0') * -1,
                left: 0,
                behavior: 'instant'
            });
        },

        /**
         * Update Carousel scroll color
         */
        setScrollColor () {
            const timer = 1800;
            if (this.animationsToggle) {
                setTimeout(() => {
                    this.scrollColor = '#333333';
                }, timer);
            } else {
                this.scrollColor = '#333333';
            }
        },

        /**
         * Init Carousel scroll color
         */
        initScrollColor () {
            this.scrollColor = '#030017'
        },

        /**
         * Update the setting page when font size changes
         */
        changeFontSize () {
            this.fontSizeSetting = this.$session.get('growFont');
        },

        /**
         * Change the period
         *
         * @param   {string}    sens      go to previous or next period
         */
        changePeriod (sens) {
            if ( (sens === 'next' && this.dates.lastOne) || (sens === 'previous' && this.dates.firstOne) ) {
                return;
            }

            this.positionLeft = 0;

            EventBus.$emit('update-dates', {
                'sens':  sens,
                'index': this.dates.index
            });
        },

        /**
         * Change Compared With Canada
         *
         * @param   {string}    sens      go to previous or next compared with canada
         */
        changeComparedWith (sens) {
            if ( (sens === 'next' && this.comparingData.region.lastOne) || (sens === 'previous' && this.comparingData.region.firstOne) ) {
                return;
            }

            if (sens ==='next' && this.positionLeft > -this.carouselWidth * this.rankingData.length) {
                setTimeout(() => this.positionLeft -= this.carouselWidth, 300);
            } else if (sens ==='previous' && this.positionLeft < 0) {
                setTimeout(() => this.positionLeft += this.carouselWidth, 300);
            } else {
                return;
            }

            EventBus.$emit('change-compare-with', {
                'sens':  sens,
                'index': this.comparingData.region.index
            });
        }
    }
}
</script>
